import {
    RECONCILIATION_ISSUE_CODES,
    reconciliationMessageOf,
} from './reconciliation-issue-messages';

import type {
    DonationsReconciliationReport,
    ReconciliationDocument,
    ReconciliationFiscalCalendar,
    ReconciliationIssue,
    ReconciliationMoney,
    ReconciliationTotalsRow,
} from '../schemas/donations-reconciliation';

/**
 * What the donations reconciliation panel shows (INI-0034 S7), as pure functions
 * of the API's answer, so every state is tested without a renderer.
 *
 * The runner decides everything: what is compared, what differs, whether a run may
 * start. This only words it. Ids, counts and amounts only — the report carries no
 * name or email, and nothing here adds one.
 */

export const DONATIONS_MODULE_ID = 'fundraising-donations';

/** Re-read every 5 s while a request is still going. */
export const RECONCILIATION_POLL_MS = 5000;

/**
 * The runner's own `RECONCILIATION_STALE_AFTER_MS`: a request still `requested` or
 * `running` after this long is stuck, and the runner lets a new one replace it.
 */
export const RECONCILIATION_STALE_AFTER_MS = 30 * 60 * 1000;

/** The refusal codes the two routes answer, as the API relays them. */
export const MODULE_NOT_AVAILABLE = 'MIGRATION_MODULE_NOT_AVAILABLE';
export const RECONCILIATION_NOT_FOUND = 'RECONCILIATION_NOT_FOUND';
export const RECONCILIATION_ALREADY_RUNNING = 'RECONCILIATION_ALREADY_RUNNING';
export const INVALID_TENANT_ID = 'INVALID_TENANT_ID';

/** The failure a read ended in, reduced to what the panel needs: status and code. */
export interface ReadFailure {
    readonly status: number | null;
    readonly code: string | null;
}

export type ReconciliationPanelState =
    | { readonly kind: 'choose-client' }
    | { readonly kind: 'loading' }
    | { readonly kind: 'module-off' }
    | { readonly kind: 'never-run' }
    | { readonly kind: 'invalid-client' }
    | { readonly kind: 'read-failed' }
    | {
          readonly kind: 'in-progress';
          readonly stale: boolean;
          /** The latest COMPLETED report, kept while this request runs. */
          readonly previous: DonationsReconciliationReport | null;
      }
    | {
          readonly kind: 'failed';
          readonly message: string;
          readonly previous: DonationsReconciliationReport | null;
      }
    | { readonly kind: 'completed'; readonly report: DonationsReconciliationReport };

/** Whether a request is still going and not stale — the runner's `isReconciliationInFlight`. */
export function isInFlight(document: ReconciliationDocument, now: number): boolean {
    if (document.state !== 'requested' && document.state !== 'running') return false;
    const since = Date.parse(document.startedAt ?? document.requestedAt);
    return Number.isFinite(since) && now - since < RECONCILIATION_STALE_AFTER_MS;
}

/**
 * One state per thing the operator can do something about.
 *
 * **The document wins over the error.** A poll that fails after a document was read
 * keeps the panel on that document (react-query keeps `data` beside `error`); the
 * failure is only a non-blocking {@link refreshNotice}, and the Run button follows the
 * document, so it stays disabled while that request is in flight. Only a read that
 * never produced a document is a state of its own: a refusal the routes name gets
 * its own kind; any other failure is `read-failed` (the shared error state shows it).
 */
export function panelState(input: {
    readonly tenantId: string | null;
    readonly document: ReconciliationDocument | undefined;
    readonly failure: ReadFailure | null;
    readonly now: number;
}): ReconciliationPanelState {
    if (input.tenantId === null) return { kind: 'choose-client' };
    const { document } = input;
    if (document === undefined) {
        if (input.failure === null) return { kind: 'loading' };
        if (input.failure.code === MODULE_NOT_AVAILABLE) return { kind: 'module-off' };
        if (input.failure.code === RECONCILIATION_NOT_FOUND) return { kind: 'never-run' };
        if (input.failure.code === INVALID_TENANT_ID) return { kind: 'invalid-client' };
        return { kind: 'read-failed' };
    }

    const previous = document.report ?? null;
    switch (document.state) {
        case 'requested':
        case 'running':
            return { kind: 'in-progress', stale: !isInFlight(document, input.now), previous };
        case 'failed':
            return {
                kind: 'failed',
                message:
                    document.failure?.message ??
                    'The reconciliation failed. Run it again; if it keeps failing, escalate it to Backend.',
                previous,
            };
        case 'completed':
            // A completed document always carries its report; without one there is
            // nothing to show, which reads like a failure the operator can retry.
            return previous === null
                ? {
                      kind: 'failed',
                      message: 'The reconciliation finished without a report. Run it again.',
                      previous: null,
                  }
                : { kind: 'completed', report: previous };
    }
}

/** A 4xx is an answer (module off, none yet, bad id): never retried, never polled again. */
export function isClientFailure(failure: ReadFailure | null): boolean {
    return failure?.status !== null && failure?.status !== undefined && failure.status < 500;
}

/**
 * Poll while a request is going; stop once it is finished or stale, and stop at once
 * when the last read was refused with a 4xx (for example the module turned off for
 * the client mid-poll), whatever the document still says.
 */
export function reconciliationPollInterval(
    document: ReconciliationDocument | undefined,
    failure: ReadFailure | null,
    now: number,
): number | false {
    if (isClientFailure(failure)) return false;
    return document !== undefined && isInFlight(document, now) ? RECONCILIATION_POLL_MS : false;
}

/**
 * The non-blocking line shown when a document is on screen but the last re-read
 * failed. `null` when there is nothing to say (no document, or no failure).
 */
export function refreshNotice(
    document: ReconciliationDocument | undefined,
    failure: ReadFailure | null,
): string | null {
    if (document === undefined || failure === null) return null;
    if (failure.code === MODULE_NOT_AVAILABLE) {
        return 'Could not refresh: the Donations migration is now off for this client. Showing the last reconciliation read.';
    }
    return isClientFailure(failure)
        ? 'Could not refresh: the migration runner refused the read. Showing the last reconciliation read.'
        : 'Could not refresh the reconciliation. Showing the last one read; it will try again.';
}

/** The Run button: an engineer, a client chosen, the module on, nothing going. */
export function canRequest(state: ReconciliationPanelState, canWrite: boolean): boolean {
    if (!canWrite) return false;
    switch (state.kind) {
        case 'never-run':
        case 'failed':
        case 'completed':
        case 'read-failed':
            return true;
        case 'in-progress':
            return state.stale;
        default:
            return false;
    }
}

/**
 * The words the live status region announces for a state. `failure` is the last
 * re-read's: when it was a 4xx, polling has stopped (see
 * {@link reconciliationPollInterval}), so a request still in flight on screen must
 * not promise that the page updates on its own.
 */
export function stateAnnouncement(
    state: ReconciliationPanelState,
    failure: ReadFailure | null = null,
): string {
    if (state.kind === 'in-progress' && !state.stale && isClientFailure(failure)) {
        const why =
            failure?.code === MODULE_NOT_AVAILABLE
                ? 'the Donations migration is now off for this client'
                : 'the migration runner refused the read';
        return `Refresh stopped: ${why}. The reconciliation shown was still running when it was last read; choose the client again to check it.`;
    }
    switch (state.kind) {
        case 'choose-client':
            return 'Choose a client to see its donations reconciliation.';
        case 'loading':
            return 'Loading the latest reconciliation…';
        case 'module-off':
            return 'The Donations migration is off for this client, so it has no reconciliation. Turn on “Fundraising Migration: Donations” for it first.';
        case 'never-run':
            return 'No reconciliation has been run for this client yet.';
        case 'invalid-client':
            return 'This client id is not valid for the migration runner.';
        case 'read-failed':
            return 'The latest reconciliation could not be read.';
        case 'in-progress':
            return state.stale
                ? 'The last reconciliation has not answered for over 30 minutes. You can run a new one.'
                : 'Reconciling. Salesforce and Veevart are being compared; this page updates on its own.';
        case 'failed':
            return `The last reconciliation did not finish. ${state.message}`;
        case 'completed':
            return state.report.outcome === 'reconciled'
                ? 'Reconciled: Salesforce and Veevart match.'
                : `${plural(state.report.issueCount, 'difference')} found. Nothing was changed in either system.`;
    }
}

// ── formatting ───────────────────────────────────────────────────────────────

/** The decimals of the backend's default currency (`DONATION_DEFAULT_CURRENCY`, USD). */
const DEFAULT_FRACTION_DIGITS = 2;

/** The currency's own decimals (`Intl`'s currency digits: USD 2, JPY 0, BHD 3); 2 when unknown. */
export function currencyFractionDigits(currency: string | null | undefined): number {
    if (!currency) return DEFAULT_FRACTION_DIGITS;
    try {
        return (
            new Intl.NumberFormat('en-US', {
                style: 'currency',
                currency,
            }).resolvedOptions().maximumFractionDigits ?? DEFAULT_FRACTION_DIGITS
        );
    } catch {
        // Not an ISO 4217 code this runtime knows: fall back rather than throw.
        return DEFAULT_FRACTION_DIGITS;
    }
}

/**
 * Minor units as an amount, with the currency's own decimals.
 *
 * ⚠️ The v2 report carries NO currency code, so the panel calls this without one and
 * gets 2 decimals — which is also what the backend wrote: its `toMinor` multiplies
 * every Salesforce amount by 100, whatever the currency. Passing a currency is for a
 * contract that names it.
 */
export function amount(minor: number, currency?: string | null): string {
    const digits = currencyFractionDigits(currency);
    return new Intl.NumberFormat('en-US', {
        minimumFractionDigits: digits,
        maximumFractionDigits: digits,
    }).format(minor / 10 ** digits);
}

/** Amounts are compared only when every copied donation uses one currency. */
export function moneyCompared(report: DonationsReconciliationReport): boolean {
    return (
        (report.issueCountsByCode[RECONCILIATION_ISSUE_CODES.TOTALS_CURRENCY_NOT_COMPARED] ?? 0) ===
        0
    );
}

export const NOT_COMPARED = 'not compared';

export function plural(count: number, one: string, many = `${one}s`): string {
    return `${count.toLocaleString('en-US')} ${count === 1 ? one : many}`;
}

const MONTHS = [
    'January',
    'February',
    'March',
    'April',
    'May',
    'June',
    'July',
    'August',
    'September',
    'October',
    'November',
    'December',
];

export interface Notice {
    readonly tone: 'info' | 'warning';
    readonly text: string;
}

/** How the totals were grouped, and when that grouping may not match Salesforce. */
export function fiscalCalendarNotice(calendar: ReconciliationFiscalCalendar): Notice {
    const month = MONTHS[calendar.startMonth - 1] ?? `month ${String(calendar.startMonth)}`;
    const named = calendar.namedByStartYear ? 'starts' : 'ends';
    const base = `Totals are grouped by the Salesforce fiscal year, which starts in ${month} and is named after the year it ${named} in.`;
    if (calendar.customYears === 0) return { tone: 'info', text: base };
    if (calendar.customYearsReadable) {
        return {
            tone: 'info',
            text: `${base} ${plural(calendar.customYears, 'custom fiscal year')} from Salesforce ${calendar.customYears === 1 ? 'is' : 'are'} applied.`,
        };
    }
    return {
        tone: 'warning',
        text: `${base} Salesforce has custom fiscal years that could not be read, so the standard fiscal year is used: the totals of a custom year may differ.`,
    };
}

/** What the lists left out. Every count stays exact; only the lists are trimmed. */
export function truncationNotices(report: DonationsReconciliationReport): string[] {
    const notices: string[] = [];
    if (report.issuesTruncated) {
        notices.push(
            `Only ${plural(report.issues.length, 'difference')} of ${report.issueCount.toLocaleString('en-US')} ${report.issues.length === 1 ? 'is' : 'are'} listed, to keep the report small. The counts by code are exact.`,
        );
    }
    if (report.pendingBalancesTruncated) {
        notices.push(
            `Only the first ${plural(report.pendingBalances.length, 'donation')} with a pending balance ${report.pendingBalances.length === 1 ? 'is' : 'are'} listed. The pending-balance counts are exact.`,
        );
    }
    return notices;
}

// ── sections ─────────────────────────────────────────────────────────────────

export interface CountLine {
    readonly label: string;
    readonly value: number;
    /** `true` when a non-zero value is something to act on. */
    readonly attention: boolean;
}

export interface CountGroup {
    readonly title: string;
    readonly lines: readonly CountLine[];
}

/** The counts, grouped the way an operator reads them: what was copied, then what to check. */
export function countGroups(report: DonationsReconciliationReport): CountGroup[] {
    const c = report.counts;
    const line = (label: string, value: number, attention = false): CountLine => ({
        label,
        value,
        attention,
    });
    return [
        {
            title: 'Donations',
            lines: [
                line('Read from Salesforce', c.salesforceDonations),
                line('Copied to Veevart', c.migrated),
                line('Created in Veevart (reconcile only)', c.v2Born),
                line('Stay in Salesforce by design', c.notMigratedByDesign),
                line('Not copied yet', c.notCopied, true),
            ],
        },
        {
            title: 'Links in Salesforce',
            lines: [
                line('Copied, donation not linked', c.stampFailed, true),
                line(
                    'Payments linked, donation not',
                    c.partialStamps.paymentLinkedDonationNot,
                    true,
                ),
                line(
                    'Donation linked, copied payments not',
                    c.partialStamps.donationLinkedPaymentsNot,
                    true,
                ),
                line('Created in Veevart, missing there', c.v2BornMissingInVeevart, true),
                line('Held twice in Veevart', c.heldTwice, true),
            ],
        },
        {
            title: 'Values',
            lines: [
                line('Amounts differ', c.amountsDiffer, true),
                line('Fund splits differ', c.fundSplitsDiffer, true),
                line('Donor not in Veevart', c.donorsMissing, true),
                line('Organization not in Veevart', c.organizationsMissing, true),
                line('Donor or organization differs', c.constituentsDiffer, true),
                line('Won but refunded in full', c.flagged.wonRefundedInFull, true),
                line(
                    'Received and written off above the amount',
                    c.flagged.receivedAndWrittenOffAboveAmount,
                    true,
                ),
            ],
        },
        {
            title: 'Pending balances',
            lines: [
                line('With a balance in Veevart', c.pendingBalance.veevartRemainingAboveZero),
                line('Warned on their last copy', c.pendingBalance.withWarning),
                line(
                    'Balance unknown (see the differences)',
                    c.pendingBalance.balanceUnknown,
                    true,
                ),
            ],
        },
    ];
}

const STAGE_LABEL: Readonly<Record<string, string>> = {
    closed_won: 'Won',
    closed_lost: 'Lost (refunded)',
    open: 'Open',
    other: 'Other',
};

export function stageLabel(stage: string): string {
    return STAGE_LABEL[stage] ?? stage;
}

const STATUS_LABEL: Readonly<Record<string, string>> = {
    paid: 'Paid',
    partially_paid: 'Partially paid',
    unpaid: 'Unpaid',
    canceled: 'Canceled',
    unknown: 'Not in Veevart',
};

export function fiscalYearLabel(fiscalYear: number | null): string {
    return fiscalYear === null ? 'No close date' : `FY ${String(fiscalYear)}`;
}

export interface TotalsLine {
    readonly key: string;
    readonly fiscalYear: string;
    readonly stage: string;
    readonly salesforce: MoneyText;
    readonly veevart: MoneyText & { readonly remaining: string };
    readonly byStatus: string;
    readonly matches: boolean;
    readonly verdict: 'Matches' | 'Differs';
}

export interface MoneyText {
    readonly count: string;
    readonly amount: string;
    readonly received: string;
    readonly refunded: string;
}

function moneyText(money: ReconciliationMoney, compared: boolean): MoneyText {
    return {
        count: money.count.toLocaleString('en-US'),
        amount: compared ? amount(money.amountMinor) : NOT_COMPARED,
        received: compared ? amount(money.receivedMinor) : NOT_COMPARED,
        refunded: compared ? amount(money.refundedMinor) : NOT_COMPARED,
    };
}

/**
 * The fiscal year × stage table, newest year first, "no close date" last. With
 * `compared: false` (donations in more than one currency, `…_TOTALS_CURRENCY_NOT_COMPARED`)
 * only the counts are shown; every amount reads "not compared", because the
 * Salesforce side is converted to the corporate currency and Veevart's is not.
 */
export function totalsLines(
    totals: readonly ReconciliationTotalsRow[],
    options: { readonly compared: boolean } = { compared: true },
): TotalsLine[] {
    const { compared } = options;
    return [...totals]
        .sort(
            (a, b) =>
                (b.fiscalYear ?? Number.NEGATIVE_INFINITY) -
                    (a.fiscalYear ?? Number.NEGATIVE_INFINITY) || a.stage.localeCompare(b.stage),
        )
        .map((row) => ({
            key: `${String(row.fiscalYear)}:${row.stage}`,
            fiscalYear: fiscalYearLabel(row.fiscalYear),
            stage: stageLabel(row.stage),
            salesforce: moneyText(row.salesforce, compared),
            veevart: {
                ...moneyText(row.veevart, compared),
                remaining: compared ? amount(row.veevart.remainingMinor) : NOT_COMPARED,
            },
            byStatus: Object.entries(row.veevartByStatus)
                .map(
                    ([status, money]) =>
                        `${STATUS_LABEL[status] ?? status} ${money.count.toLocaleString('en-US')}`,
                )
                .join(' · '),
            matches: row.matches,
            verdict: row.matches ? 'Matches' : 'Differs',
        }));
}

/** Fields whose values are money in minor units; the rest (currency, a count) are shown as given. */
const MONEY_FIELDS = new Set(['amount', 'received', 'refunded', 'pendingBalance', 'fund']);

const FIELD_LABEL: Readonly<Record<string, string>> = {
    amount: 'Amount',
    received: 'Received',
    refunded: 'Refunded',
    currency: 'Currency',
    count: 'Count',
    fund: 'Fund amount',
    pendingBalance: 'Pending balance',
    donorRef: 'Donor',
    organizationRef: 'Organization',
    payerOrDonorRef: 'Payer or donor',
};

/**
 * What each side of a compared value is, per code. Most issues put Salesforce's value
 * beside Veevart's; a few compare two Salesforce values, and saying "Veevart" there
 * would send the operator to the wrong system.
 */
interface SideLabels {
    readonly label?: string;
    readonly salesforce: string;
    readonly veevart: string;
}

const DEFAULT_SIDES: SideLabels = { salesforce: 'Salesforce', veevart: 'Veevart' };

const SIDES_BY_CODE: Readonly<Record<string, SideLabels>> = {
    // `salesforceValue` = the amount, `veevartValue` = received + written off, BOTH as
    // Salesforce's own payments show them (`donation-checks.ts`).
    [RECONCILIATION_ISSUE_CODES.RECEIVED_AND_WRITTEN_OFF_ABOVE_AMOUNT]: {
        label: 'In Salesforce',
        salesforce: 'amount',
        veevart: 'received + written off',
    },
    // Only `salesforceValue`: the refunded amount Salesforce holds.
    [RECONCILIATION_ISSUE_CODES.WON_REFUNDED_IN_FULL]: {
        label: 'In Salesforce',
        salesforce: 'refunded',
        veevart: 'Veevart',
    },
    // Only `veevartValue`: the balance Veevart shows now (the warning is a flag, not a value).
    [RECONCILIATION_ISSUE_CODES.PENDING_BALANCE_DIFFERS]: {
        salesforce: 'Salesforce',
        veevart: 'Veevart now',
    },
};

export interface IssueLine {
    readonly key: string;
    readonly code: string;
    readonly message: string;
    /** The ids the issue names, as `label: id` pairs. */
    readonly refs: readonly string[];
    /** `Received — Salesforce 100.00 · Veevart 90.00`, when the issue compares values. */
    readonly comparison: string | null;
}

function valueText(field: string | undefined, value: number | string | null | undefined): string {
    if (value === null || value === undefined) return '—';
    if (typeof value === 'number' && field !== undefined && MONEY_FIELDS.has(field)) {
        return amount(value);
    }
    return typeof value === 'number' ? value.toLocaleString('en-US') : value;
}

export function issueLine(issue: ReconciliationIssue, index: number): IssueLine {
    const refs: string[] = [];
    if (issue.opportunityId) refs.push(`Salesforce donation ${issue.opportunityId}`);
    if (issue.contributionId) refs.push(`Veevart donation ${issue.contributionId}`);
    if (issue.commitmentId) refs.push(`Veevart balance ${issue.commitmentId}`);
    if (issue.constituentId) refs.push(`Veevart contact or organization ${issue.constituentId}`);
    if (issue.fundId) refs.push(`Fund ${issue.fundId}`);
    if (issue.fiscalYear !== undefined) refs.push(fiscalYearLabel(issue.fiscalYear));
    if (issue.stage) refs.push(stageLabel(issue.stage));
    if (issue.paymentIds && issue.paymentIds.length > 0) {
        const total = issue.paymentCount ?? issue.paymentIds.length;
        const more = total - issue.paymentIds.length;
        refs.push(
            `Payments ${issue.paymentIds.join(', ')}${more > 0 ? ` and ${more.toLocaleString('en-US')} more` : ''}`,
        );
    }

    const sides = SIDES_BY_CODE[issue.code] ?? DEFAULT_SIDES;
    const label =
        sides.label ?? (issue.field ? (FIELD_LABEL[issue.field] ?? issue.field) : 'Value');
    const parts: string[] = [];
    if (issue.salesforceValue !== undefined) {
        parts.push(`${sides.salesforce} ${valueText(issue.field, issue.salesforceValue)}`);
    }
    if (issue.veevartValue !== undefined) {
        parts.push(`${sides.veevart} ${valueText(issue.field, issue.veevartValue)}`);
    }
    return {
        key: `${issue.code}:${String(index)}`,
        code: issue.code,
        message: reconciliationMessageOf(issue.code),
        refs,
        comparison: parts.length > 0 ? `${label} — ${parts.join(' · ')}` : null,
    };
}

export interface CodeCount {
    readonly code: string;
    readonly message: string;
    readonly count: number;
}

/** The exact count of every code, most frequent first — complete even when the list is trimmed. */
export function codeCounts(report: DonationsReconciliationReport): CodeCount[] {
    return Object.entries(report.issueCountsByCode)
        .filter(([, count]) => count > 0)
        .sort(([a, x], [b, y]) => y - x || a.localeCompare(b))
        .map(([code, count]) => ({ code, message: reconciliationMessageOf(code), count }));
}
